import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { ds } from '@/components/lib/design.system';
import {
    checkIfAnchorProjection,
    checkIfBoostedProjection,
    checkIfEdgeProjection,
    checkIfFreePickProjection,
    checkIfNukeProjection,
    checkIfSpecialProjection,
} from '@/feature/lobby/utils/lineupsUtils';
import { ProjectionType } from '@/types/api.generated';

const computeNonRegularProjections = (projections?: PlayerProjectionFragment[] | undefined) => {
    let boostedCount = 0;
    let discountedCount = 0;
    let edgeCount = 0;
    let anchorCount = 0;
    let nukeCount = 0;
    let hasBoostedProjections = false;
    let hasSpecialIncreasedProjections = false;
    let hasSpecialDecreasedProjections = false;
    let hasEdgeProjections = false;
    let hasAnchorProjections = false;
    let hasNukeProjections = false;
    let hasFreePickProjections = false;

    for (const projection of projections ?? []) {
        if (!('type' in projection)) {
            continue;
        }
        if (checkIfBoostedProjection(projection.type)) {
            hasBoostedProjections = true;
            boostedCount += 1;
        } else if (checkIfSpecialProjection(projection.type)) {
            if (projection.nonRegularPercentage !== undefined) {
                discountedCount += 1;
                if (projection.nonRegularPercentage > 0) {
                    hasSpecialIncreasedProjections = true;
                } else {
                    hasSpecialDecreasedProjections = true;
                }
            }
        } else if (checkIfEdgeProjection(projection.type)) {
            hasEdgeProjections = true;
            edgeCount += 1;
        } else if (checkIfAnchorProjection(projection.type)) {
            hasAnchorProjections = true;
            anchorCount += 1;
        } else if (checkIfNukeProjection(projection.type)) {
            hasNukeProjections = true;
            nukeCount += 1;
        } else if (checkIfFreePickProjection(projection.type)) {
            hasFreePickProjections = true;
        }
    }

    const hasDiscountedProjections =
        hasSpecialDecreasedProjections || hasSpecialIncreasedProjections || hasFreePickProjections;

    return {
        hasBoostedProjections,
        hasNukeProjections,
        hasSpecialIncreasedProjections,
        hasSpecialDecreasedProjections,
        hasFreePickProjections,
        hasAnchorProjections,
        hasEdgeProjections,
        hasDiscountedProjections,
        boostedCount,
        discountedCount,
        edgeCount,
        anchorCount,
        nukeCount,
    };
};

/**
 * Cache computed non-regular projections for each unique array of projections. This is a performance optimization to avoid recomputing the same data for the same array reference,
 * as this turns out to be expensive when sorting large list of players.
 * Non-refrenced entries are collected by the GC from this WeakMap.
 */
const nonRegularProjectionsCache = new WeakMap<
    PlayerProjectionFragment[],
    ReturnType<typeof computeNonRegularProjections>
>();

export const getNonRegularProjections = (projections?: PlayerProjectionFragment[] | undefined) => {
    if (!projections) {
        return computeNonRegularProjections(projections);
    }

    const cached = nonRegularProjectionsCache.get(projections);
    if (cached) {
        return cached;
    }

    const result = computeNonRegularProjections(projections);
    nonRegularProjectionsCache.set(projections, result);
    return result;
};

export const getColorByProjType = (projectionType: ProjectionType, specialIncrease?: boolean) => {
    if (checkIfBoostedProjection(projectionType) || checkIfNukeProjection(projectionType)) {
        return ds.colorToken.background.pickType.boost.strong;
    }

    if (checkIfAnchorProjection(projectionType)) {
        return ds.colorToken.background.pickType.anchor.strong;
    }

    if (checkIfFreePickProjection(projectionType)) {
        return ds.colorToken.background.pickType.freePick.strong;
    }

    if (checkIfSpecialProjection(projectionType)) {
        return specialIncrease
            ? ds.colorToken.background.pickType.surge.strong
            : ds.colorToken.background.pickType.discount.strong;
    }

    if (checkIfEdgeProjection(projectionType)) {
        return ds.colorToken.background.pickType.edge.strong;
    }

    return ds.colorToken.text.primary;
};
